import * as React from 'react';
import { Box, Tooltip, SxProps, Theme } from '@mui/material';

export interface TabButtonProps {
  /**
   * Icona da mostrare nel tab
   */
  icon: React.ReactNode;

  /**
   * Label per il tooltip
   */
  label: string;

  /**
   * Se il tab è attivo
   */
  active?: boolean;

  /**
   * Funzione chiamata al click
   */
  onClick?: () => void;

  /**
   * Se il tab è disabilitato
   */
  disabled?: boolean;

  /**
   * Colore quando il tab è attivo
   * @default 'primary.main'
   */
  activeColor?: string;

  /**
   * Colore del testo quando il tab è attivo
   * @default 'white'
   */
  activeTextColor?: string;

  /**
   * Colore quando il tab non è attivo
   * @default 'background.paper'
   */
  inactiveColor?: string;

  /**
   * Colore del testo quando il tab non è attivo
   * @default 'text.primary'
   */
  inactiveTextColor?: string;

  /**
   * Abilita animazione di scala al hover/active
   * @default true
   */
  enableScaleAnimation?: boolean;

  /**
   * Props sx aggiuntive per personalizzare lo stile
   */
  sx?: SxProps<Theme>;
}

/**
 * Pulsante tab con animazioni e stati attivi
 * 
 * @example
 * ```tsx
 * <TabButton
 *   icon={<HomeIcon />}
 *   label="Home"
 *   active={activeTab === 'home'}
 *   onClick={() => setActiveTab('home')}
 * />
 * ```
 */
export const TabButton: React.FC<TabButtonProps> = ({
  icon,
  label,
  active = false,
  onClick,
  disabled = false,
  activeColor = 'primary.main',
  activeTextColor = 'white',
  inactiveColor = 'background.paper',
  inactiveTextColor = 'text.primary',
  enableScaleAnimation = true,
  sx = {},
}) => {
  const button = (
    <Box
      component="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 1,
        py: 1,
        backgroundColor: active ? activeColor : inactiveColor,
        color: active ? activeTextColor : inactiveTextColor,
        border: 'none',
        borderRadius: active ? 5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        transition: enableScaleAnimation
          ? 'all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          : 'all 0.2s',
        transform: enableScaleAnimation && active ? 'scale(1.1)' : 'scale(1)',
        opacity: disabled ? 0.5 : 1,
        '&:hover': {
          transform: enableScaleAnimation && !disabled ? 'scale(1.05)' : undefined,
          backgroundColor: !disabled && !active ? `${activeColor}22` : undefined,
        },
        ...sx,
      }}
    >
      {icon}
    </Box>
  );

  return <Tooltip title={label}>{button}</Tooltip>;
};

